// ============================================================
// useTurnTrackerSync - the turn order, converging across writers.
//
// The tracker is ONE JSON document that every client rewrites whole,
// and since the action economy (v8.309.0) every plate press on every
// client is a write. Last-write-wins on a whole array is how a removed
// participant walked back in: a peer still holding the old array wrote
// it back, and waiting ten seconds between removals "mostly" avoided it
// because the peers had caught up by then.
//
// So (v8.310.6) a change is sent as an EDIT, not a state: the pair
// (base, next) the caller derived, replayed on the server's current
// document inside a compare-and-set (`transactDoc`) through
// `mergeTrackerChange`. Whatever the server holds by then - another
// chip charged, another arrival - survives; what this edit removed
// stays removed. A whole-state replace (undo) is the one exception, by
// name.
//
// Three rules from the battlefield's sync, which met the same problem
// first:
//   - a remote view is not news while a flush is out or pending: the
//     flush's result is adopted instead, and its SSE echo is then a
//     no-op by JSON comparison;
//   - the echo cache belongs to one session;
//   - a flush that finally fails is reported, and the screen goes back
//     to the server's view, so the client cannot believe a write the
//     server never saw.
//
// A VIEW SET ASIDE IS NOT A VIEW FORGOTTEN (fuzz seed 501, after network
// trouble). Views heard while a write is out are kept, in order, and
// weighed when the writes are done: on a failure the newest is shown
// (Firestore never re-delivers a document that did not change, so
// waiting for "the next view" left an unsaved edit on screen for good);
// on success the reply is shown unless the stream had already carried
// the reply's own echo AND something after it, which is newer. And a
// failure flushes the edits queued behind it - it used to leave them
// queued, and while the queue held anything every view was set aside,
// for ever.
// ============================================================

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { MutableRefObject } from 'react';
import { emptyTurnTracker } from '../engine/factories';
import { mergeTrackerChange, normalizeTurnTrackerState } from '../engine/turnTracker';
import { diag, errorSummary } from '../diagnostics/log';
import type { TurnTrackerState } from '../types';

const TRACKER_SYNC_DEBOUNCE_MS = 200;

/** One queued write: an edit to replay, or a state to impose. */
type TrackerOp = (server: TurnTrackerState | null) => TurnTrackerState;

interface UseTurnTrackerSyncParams {
  sessionId: string | null;
  mp: any; // Multiplayer service (DI)
}

export interface UseTurnTrackerSyncReturn {
  turnTrackerState: TurnTrackerState;
  setTurnTrackerState: React.Dispatch<React.SetStateAction<TurnTrackerState>>;
  /** Send an edit: what the caller started from, and what it made of it. */
  syncTurnTrackerChange: (base: TurnTrackerState, next: TurnTrackerState) => void;
  /** Impose a whole state - undo's restore, and nothing else should. */
  syncTurnTrackerToFirestore: (state: TurnTrackerState) => void;
  lastTurnTrackerJsonRef: MutableRefObject<string>;
}

/** The sync's refs and collaborators - the listener, the flush and the queue share them. */
interface TrackerSyncContext {
  /** Echo prevention: the JSON the server is believed to hold. */
  lastJsonRef: MutableRefObject<string>;
  /** The newest view the listener delivered, shown or set aside. */
  newestRef: MutableRefObject<TurnTrackerState | null>;
  /** The JSON of every view set aside while writing, oldest first. */
  heardRef: MutableRefObject<string[]>;
  pendingRef: MutableRefObject<TrackerOp[]>;
  inFlightRef: MutableRefObject<number>;
  initRef: MutableRefObject<boolean>;
  /** The flush reads the CURRENT session, never the one it was scheduled under. */
  sessionIdRef: MutableRefObject<string | null>;
  mp: any; // Multiplayer service (DI)
  setState: (state: TurnTrackerState) => void;
}

/** Show a state as the server's: the echo cache learns it, the screen shows it. */
function show(ctx: TrackerSyncContext, data: TurnTrackerState): void {
  ctx.lastJsonRef.current = JSON.stringify(data);
  ctx.setState(data);
}

/**
 * A server view, adopted unless it is our own echo - and SET ASIDE while
 * this client has a write out or waiting: the flush returns the merged
 * truth, and adopting a view from before it would walk a fresh removal
 * backwards (v8.115.0 by another door). Set aside, not dropped: the
 * settle weighs it (see the file header).
 */
function adoptView(ctx: TrackerSyncContext, data: TurnTrackerState): void {
  ctx.initRef.current = true;
  ctx.newestRef.current = data;
  const json = JSON.stringify(data);
  if (ctx.inFlightRef.current > 0 || ctx.pendingRef.current.length > 0) {
    ctx.heardRef.current.push(json);
    return;
  }
  if (json !== ctx.lastJsonRef.current) show(ctx, data);
}

/**
 * The reply, unless the stream carried the reply's own echo while it was
 * out AND something after it: the stream runs in the server's order, so
 * what followed the echo is newer (a slow or retried reply, the GM's next
 * move). A view equal to the reply from before the write cannot fool this
 * for long - the real echo still arrives, and is news.
 */
function newestOf(ctx: TrackerSyncContext, reply: TurnTrackerState): TurnTrackerState {
  const heard = ctx.heardRef.current;
  ctx.heardRef.current = [];
  const echo = heard.lastIndexOf(JSON.stringify(reply));
  const later = echo >= 0 && echo < heard.length - 1;
  return later && ctx.newestRef.current ? ctx.newestRef.current : reply;
}

/**
 * One listener delivery. An older client writes participants keyed by
 * `tabId`; normalizing accepts those rather than silently unlinking every
 * turn (v6.58.0). No document yet needs no init write - the tracker
 * starts inactive.
 */
function applyServerView(ctx: TrackerSyncContext, raw: TurnTrackerState | null, exists: boolean): void {
  const data = normalizeTurnTrackerState(raw);
  if (exists && data) adoptView(ctx, data);
  else if (!exists) ctx.initRef.current = true;
}

/**
 * The flush came back. If more arrived while it was out, they replay on
 * what was just written and THAT flush's result is the one to adopt;
 * otherwise adopt this one - or a view heard after it. A session changed
 * meanwhile adopts nothing.
 */
function settleFlush(ctx: TrackerSyncContext, sid: string, result: TurnTrackerState | null): void {
  ctx.inFlightRef.current -= 1;
  if (ctx.sessionIdRef.current !== sid) return;
  if (ctx.pendingRef.current.length > 0) {
    flushTracker(ctx);
    return;
  }
  const data = normalizeTurnTrackerState(result);
  if (data) show(ctx, newestOf(ctx, data));
}

/**
 * The server never saw these - say so. The edits queued behind them still
 * go (each is replayed on whatever the server holds); with none left, the
 * screen shows the newest server view, not the edit that did not save.
 */
function failFlush(ctx: TrackerSyncContext, sid: string, err: unknown): void {
  ctx.inFlightRef.current -= 1;
  diag.error('turns', 'tracker write failed - back to the server view', errorSummary(err));
  if (typeof ctx.mp.reportWriteFailure === 'function') ctx.mp.reportWriteFailure('The turn order did not save.', err);
  if (ctx.sessionIdRef.current !== sid) return;
  if (ctx.pendingRef.current.length > 0) {
    flushTracker(ctx);
    return;
  }
  ctx.heardRef.current = [];
  if (ctx.newestRef.current) show(ctx, ctx.newestRef.current);
  else ctx.lastJsonRef.current = '';
}

/** The flush: every queued op, in order, inside one compare-and-set. */
function flushTracker(ctx: TrackerSyncContext): void {
  const sid = ctx.sessionIdRef.current;
  const ops = ctx.pendingRef.current;
  ctx.pendingRef.current = [];
  if (!sid || ops.length === 0) return;
  ctx.inFlightRef.current += 1;
  ctx.mp
    .transactSharedTurnTracker(sid, (server: TurnTrackerState | null) =>
      ops.reduce<TurnTrackerState | null>((s, op) => op(s), server),
    )
    .then((result: TurnTrackerState | null) => settleFlush(ctx, sid, result))
    .catch((err: unknown) => failFlush(ctx, sid, err));
}

/**
 * The listener. AN ECHO CACHE IS ONLY VALID FOR ONE SESSION (v8.115.0's
 * cousin): carrying it across meant the new session's first document could
 * be mistaken for the old one's echo, so a session change resets it.
 */
function useTrackerListener(sessionId: string | null, ctx: TrackerSyncContext) {
  useEffect(() => {
    if (!sessionId) return;
    ctx.lastJsonRef.current = '';
    ctx.newestRef.current = null;
    ctx.heardRef.current = [];
    ctx.initRef.current = false;
    ctx.pendingRef.current = [];
    const unsub = ctx.mp.onSharedTurnTrackerUpdate(sessionId, (raw: TurnTrackerState | null, exists: boolean) =>
      applyServerView(ctx, raw, exists),
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
}

/** The debounced queue: an op joins the next flush, which waits for one in progress to return. */
function useEnqueue(ctx: TrackerSyncContext, syncTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>) {
  const flush = useCallback(() => flushTracker(ctx), [ctx]);
  return useCallback(
    (op: TrackerOp) => {
      if (!ctx.sessionIdRef.current) return;
      ctx.pendingRef.current.push(op);
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        syncTimerRef.current = null;
        if (ctx.inFlightRef.current === 0) flush();
        // else: the flush in progress will pick these up when it returns.
      }, TRACKER_SYNC_DEBOUNCE_MS);
    },
    [ctx, flush, syncTimerRef],
  );
}

/**
 * The refs, and one context object over them - rebuilt only when `mp`
 * changes, since the refs and the setter never do. So the flush and the
 * queue are rebuilt exactly when they always were.
 */
function useTrackerSyncContext(sessionId: string | null, mp: any, setState: TrackerSyncContext['setState']) {
  const lastJsonRef = useRef('');
  const newestRef = useRef<TurnTrackerState | null>(null);
  const heardRef = useRef<string[]>([]);
  const pendingRef = useRef<TrackerOp[]>([]);
  const inFlightRef = useRef(0);
  const initRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  return useMemo<TrackerSyncContext>(
    () => ({ lastJsonRef, newestRef, heardRef, pendingRef, inFlightRef, initRef, sessionIdRef, mp, setState }),
    [mp, setState],
  );
}

/** The turn order, converging across writers - see the file header. */
export default function useTurnTrackerSync({ sessionId, mp }: UseTurnTrackerSyncParams): UseTurnTrackerSyncReturn {
  const [turnTrackerState, setTurnTrackerState] = useState<TurnTrackerState>(emptyTurnTracker);
  const ctx = useTrackerSyncContext(sessionId, mp, setTurnTrackerState);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useTrackerListener(sessionId, ctx);
  const enqueue = useEnqueue(ctx, syncTimerRef);
  const syncTurnTrackerChange = useCallback(
    (base: TurnTrackerState, next: TurnTrackerState) => {
      if (JSON.stringify(base) === JSON.stringify(next)) return;
      enqueue((server) => mergeTrackerChange(server, base, next));
    },
    [enqueue],
  );
  const syncTurnTrackerToFirestore = useCallback((state: TurnTrackerState) => enqueue(() => state), [enqueue]);
  return {
    turnTrackerState,
    setTurnTrackerState,
    syncTurnTrackerChange,
    syncTurnTrackerToFirestore,
    lastTurnTrackerJsonRef: ctx.lastJsonRef,
  };
}
